import React, { useMemo, useState, useEffect } from "react";
import { v4 as uuidv4 } from "uuid";
import dayjs from "dayjs";
import "dayjs/locale/en";

import { AddTransactionForm } from "../../components/AddTransaction/AddTransactionForm.tsx";
import TransactionItem from "../../components/TransactionItem.tsx";
import { useTransactions } from "../../hooks/useTransactions.tsx";
import { TransactionFormData } from "../../types/transaction.tsx";
import Input from "../../components/ui/input.tsx";
import Button from "../../components/ui/button.tsx";
import { SimpleSelect } from "../../components/ui/select-shadcn.tsx";
import { Plus, SlidersHorizontal } from "lucide-react";
import Modal from "../../components/Modal/index.tsx";
import Label from "../../components/ui/label.tsx";
import { categories } from "../../data/categories.tsx";
import { transactionTypes } from "../../data/transactionTypes.tsx";
import { useSearchParams } from "react-router-dom";

dayjs.locale("en");

export default function Loans() {
  const [showModal, setShowModal] = useState(false);
  const [filterType, setFilterType] = useState<string>("all");
  const [searchText, setSearchText] = useState<string>("");
  const [showFilters, setShowFilters] = useState(false);
  const [period, setPeriod] = useState<string>("month");
  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const FILTERS_STORAGE_KEY = "filters:loans";
  const [searchParams, setSearchParams] = useSearchParams();

  const { transactions, addTransaction } = useTransactions();

  // Initialize filters: URL params take precedence, then localStorage
  useEffect(() => {
    const hasURL = ["s", "t", "c", "p", "f", "to"].some((k) =>
      searchParams.has(k)
    );
    if (hasURL) {
      const s = searchParams.get("s") ?? "";
      const t = searchParams.get("t") ?? "all";
      const c = searchParams.get("c") ?? "all";
      const p = searchParams.get("p") ?? "month";
      const f = searchParams.get("f") ?? "";
      const toq = searchParams.get("to") ?? "";
      setSearchText(s);
      setFilterType(t);
      setSelectedCategory(c);
      setPeriod(p);
      setDateFrom(f);
      setDateTo(toq);
      return;
    }
    try {
      const raw = localStorage.getItem(FILTERS_STORAGE_KEY);
      if (raw) {
        const f = JSON.parse(raw);
        setSearchText(f.searchText ?? "");
        setFilterType(f.filterType ?? "all");
        setSelectedCategory(f.selectedCategory ?? "all");
        setPeriod(f.period ?? "month");
        setDateFrom(f.dateFrom ?? "");
        setDateTo(f.dateTo ?? "");
      }
    } catch {}
  }, [searchParams]);

  // Persist filters
  useEffect(() => {
    try {
      const f = {
        searchText,
        filterType,
        selectedCategory,
        period,
        dateFrom,
        dateTo,
      };
      localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(f));
    } catch {}
  }, [searchText, filterType, selectedCategory, period, dateFrom, dateTo]);

  // Sync filters to URL
  useEffect(() => {
    const params: Record<string, string> = {};
    if (searchText.trim() !== "") params.s = searchText.trim();
    if (filterType !== "all") params.t = filterType;
    if (selectedCategory !== "all") params.c = selectedCategory;
    if (period !== "month") params.p = period;
    if (period === "range") {
      if (dateFrom) params.f = dateFrom;
      if (dateTo) params.to = dateTo;
    }
    setSearchParams(params, { replace: true });
  }, [searchText, filterType, selectedCategory, period, dateFrom, dateTo, setSearchParams]);

  const handleAddTransaction = (data: TransactionFormData) => {
    addTransaction({
      ...data,
      id: uuidv4(),
      category: data.category ?? "miscellaneous",
      status: data.status ?? "pending",
    });
    setShowModal(false);
  };

  const handleError = (error: unknown) => {
    console.error("Error adding transaction:", error);
  };

  const filteredTransactions = useMemo(() => {
    const now = dayjs();
    const presetStart = (() => {
      if (period === "7d") return now.subtract(7, "day");
      if (period === "30d") return now.subtract(30, "day");
      if (period === "month") return now.startOf("month");
      return null; // all or range
    })();

    return transactions
      .filter((tx) => (filterType === "all" ? true : tx.type === filterType))
      .filter((tx) =>
        selectedCategory && selectedCategory !== "all"
          ? tx.category === selectedCategory
          : true
      )
      .filter((tx) => tx.title.toLowerCase().includes(searchText.toLowerCase()))
      .filter((tx) => {
        const dt = dayjs(tx.date);
        if (period === "range") {
          const from = dateFrom ? dayjs(dateFrom).startOf("day") : null;
          const to = dateTo ? dayjs(dateTo).endOf("day") : null;
          if (from && dt.isBefore(from)) return false;
          if (to && dt.isAfter(to)) return false;
          return true;
        }
        return presetStart ? dt.isAfter(presetStart) : true;
      });
  }, [
    transactions,
    filterType,
    selectedCategory,
    searchText,
    period,
    dateFrom,
    dateTo,
  ]);

  return (
    <section className="p-5 flex justify-center">
      <div className="container-site">
        {/* Header */}
        <div className="flex flex-wrap gap-5 justify-between items-center mb-4">
          <div>
            <h3 className="heading-2">Loans</h3>
            <p className="sub-heading-2">
              View loan balances and repayments at a glance
            </p>
          </div>

          <div className="flex items-center gap-2">
            <Input
              type="text"
              placeholder="Search title..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="w-56"
            />
            <Button
              size="icon"
              onClick={() => setShowModal(true)}
              aria-label="Add transaction"
              title="Add Transaction"
            >
              <Plus className="h-4 w-4" />
            </Button>
            <Button
              variant="secondary"
              size="icon"
              aria-label="Open filters"
              title="Filters"
              onClick={() => setShowFilters(true)}
              className="relative"
            >
              <SlidersHorizontal className="h-4 w-4" />
              {useMemo(() => {
                let count = 0;
                if (searchText.trim() !== "") count++;
                if (filterType !== "all") count++;
                if (selectedCategory !== "all") count++;
                if (period !== "month") count++;
                if (period === "range" && (dateFrom || dateTo)) count++;
                return count > 0 ? (
                  <span className="absolute -top-1 -right-1 min-w-[1.1rem] h-4 px-1 rounded-full bg-indigo-600 text-[10px] leading-4 text-white text-center">
                    {count}
                  </span>
                ) : null;
              }, [
                searchText,
                filterType,
                selectedCategory,
                period,
                dateFrom,
                dateTo,
              ])}
            </Button>
          </div>
        </div>

        <Modal
          isOpen={showFilters}
          onClose={() => setShowFilters(false)}
          title="Filters"
        >
          <form
            className="space-y-5"
            onSubmit={(e) => {
              e.preventDefault();
              setShowFilters(false);
            }}
          >
            <div>
              <Label className="mb-2 block">Quick Period</Label>
              <div className="flex flex-wrap gap-2">
                {[
                  { id: "month", label: "This Month" },
                  { id: "7d", label: "Last 7 Days" },
                  { id: "30d", label: "Last 30 Days" },
                  { id: "all", label: "All Time" },
                  { id: "range", label: "Custom Range" },
                ].map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setPeriod(p.id)}
                    className={[
                      "px-3 py-1.5 rounded-md text-sm border",
                      period === p.id
                        ? "bg-indigo-600 text-white border-indigo-500"
                        : "bg-white/5 text-white/90 border-white/15 hover:bg-white/10",
                    ].join(" ")}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Search moved to header for seamless typing */}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="mb-1 block">Type</Label>
                <SimpleSelect
                  value={filterType}
                  onValueChange={(v) => setFilterType(v)}
                  options={[
                    { label: "All Types", value: "all" },
                    ...transactionTypes.map((t) => ({
                      label: t.name,
                      value: t.id,
                    })),
                  ]}
                />
              </div>
              <div>
                <Label className="mb-1 block">Category</Label>
                <SimpleSelect
                  value={selectedCategory}
                  onValueChange={(v) => setSelectedCategory(v)}
                  options={[
                    { label: "All Categories", value: "all" },
                    ...categories.map((c) => ({ label: c.name, value: c.id })),
                  ]}
                />
              </div>
            </div>

            {period === "range" && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="from" className="mb-1 block">
                    From
                  </Label>
                  <Input
                    id="from"
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="to" className="mb-1 block">
                    To
                  </Label>
                  <Input
                    id="to"
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                  />
                </div>
              </div>
            )}

            {useMemo(() => {
              if (period === "range" && dateFrom && dateTo) {
                const invalid = dayjs(dateFrom).isAfter(dayjs(dateTo));
                return invalid ? (
                  <p className="text-red-400 text-sm">
                    From date cannot be after To date.
                  </p>
                ) : null;
              }
              return null;
            }, [period, dateFrom, dateTo])}

            <div className="pt-2 flex justify-end gap-2">
              <Button
                variant="ghost"
                onClick={() => {
                  setSearchText("");
                  setFilterType("all");
                  setSelectedCategory("all");
                  setPeriod("month");
                  setDateFrom("");
                  setDateTo("");
                }}
              >
                Reset
              </Button>
              <Button type="submit">Apply</Button>
            </div>
          </form>
        </Modal>

        {filteredTransactions.length === 0 ? (
          <p className="text-slate-500 text-center py-6">
            No matching transactions found.
          </p>
        ) : (
          <div className="space-y-3">
            {filteredTransactions.map((tx) => (
              <TransactionItem
                key={tx.id}
                title={tx.title}
                date={tx.date}
                type={tx.type}
                amount={tx.amount}
                currency={tx.currency}
                categoryId={tx.category}
              />
            ))}
          </div>
        )}

        <Modal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          title="Add Transaction"
        >
          <AddTransactionForm onSubmit={handleAddTransaction} onError={handleError} />
        </Modal>
      </div>
    </section>
  );
}
